import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config/api';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  useToast,
  Container,
  Card,
  CardBody,
} from '@chakra-ui/react';
import { getEmailError } from '../utils/emailValidation';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const [cooldown, setCooldown] = useState(0);

  // Gestion du cooldown pour éviter le spam
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation frontend de l'email
    const emailError = getEmailError(email);
    if (emailError) {
      toast({
        title: 'Email invalide',
        description: emailError,
        status: 'error',
        duration: 5000,
      });
      return;
    }

    setIsLoading(true);

    try {
      const minDelay = new Promise(resolve => setTimeout(resolve, 500));
      
      const [response] = await Promise.all([
        fetch(`${API_URL}/auth/forgot-password`, {        
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email }),
        }),
        minDelay
      ]);

      const data = await response.json();

      setIsSubmitted(true);
      setCooldown(60);

      if (response.ok) {
        setIsSubmitted(true);
        setCooldown(60); // 60 secondes de cooldown
      } else {
        toast({
          title: 'Erreur',
          description: data.error || 'Une erreur est survenue',
          status: 'error',
          duration: 5000,
        });
      }
    } catch (error) {
      setIsSubmitted(true);  
      toast({
        title: 'Information',
        description: 'Si un compte existe avec cette adresse email, vous recevrez un lien de réinitialisation.',
        status: 'info',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <Container maxW="md" py={12}>
        <Card>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Heading size="lg" textAlign="center" color="eecf.500">
                Email envoyé
              </Heading>
              <Text textAlign="center">
                Si un compte existe avec cette adresse email, vous recevrez un lien de réinitialisation dans quelques instants.
              </Text>
              <Text textAlign="center" fontSize="sm" color="gray.600">
                Vérifiez également votre dossier spam.
              </Text>
              <Button
                colorScheme="blue"
                onClick={() => navigate('/login')}
              >
                Retour à la connexion
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxW="md" py={12}>
      <Card>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <VStack spacing={4} align="stretch">
              <Heading size="lg" textAlign="center" color="eecf.500">
                Mot de passe oublié
              </Heading>
              <Text textAlign="center" color="gray.600">
                Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
              </Text>

              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre.email@example.com"
                />
              </FormControl>

              <Button
                type="submit"
                colorScheme="blue"
                isLoading={isLoading}
                loadingText="Envoi en cours..."
              >
                Envoyer le lien
              </Button>

              <Button
                variant="ghost"
                onClick={() => navigate('/login')}
              >
                Retour à la connexion
              </Button>
            </VStack>
          </form>
        </CardBody>
      </Card>
    </Container>
  );
}